import { useState, useEffect, useRef, useCallback } from 'react';
import { createKalshiClient } from '../lib/kalshiApi';
import type { KalshiOrder } from '../lib/kalshiApi';
import { initAuth } from '../lib/kalshiAuth';
import { OrderMonitor } from '../lib/orderMonitor';
import { createUserStream, type UserStream } from '../lib/userStream';
import { PositionTracker } from '../lib/positionTracker';
import { QueueVelocityTracker, type VelocityResult } from '../lib/queueVelocity';
import type { KalshiOrderbook } from '../types';
import type { NBAMarketRow, FillNotification, KalshiCredentials } from '../types';
import { perfTracker } from '../lib/perf-tracker';
import { playFillTone, playWhaleTone, playKaChingTone } from '../lib/tradeAudio';
import { readSoundSettings } from '../lib/soundSettings';

export interface ActivityEvent {
  type:
    | 'order_placed'
    | 'order_filled'
    | 'order_cancelled'
    | 'queue_velocity'
    | 'connection_change'
    | 'connection_lost'
    | 'connection_restored'
    | 'error';
  message: string;
  amount?: number;
  orderId?: string;
  ticker?: string;
}

export interface OrderManagementParams {
  isConnected: boolean;
  apiClientRef: React.MutableRefObject<Awaited<ReturnType<typeof createKalshiClient>> | null>;
  credentials: KalshiCredentials | null;
  positionTrackerRef: React.MutableRefObject<PositionTracker>;
  marketsDataRef: React.MutableRefObject<Map<string, NBAMarketRow>>;
  refreshPositions: () => void;
  loadPositions: () => Promise<void>;
  setErrorMessage: React.Dispatch<React.SetStateAction<string | null>>;
  portfolioPollingEnabled: boolean;
  setPortfolioPollingEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  /** Optional callback to push events to ActivityContext */
  onActivity?: (event: ActivityEvent) => void;
}

/**
 * Compute absolute queue depth for an order.
 * Sums all contracts at better prices + queue position at own price level.
 *
 * For BUY orders on a side, "better price" means higher price bids ahead in queue.
 * The orderbook levels for that side are sorted descending by price.
 */
export function computeAbsoluteQueueDepth(
  order: KalshiOrder,
  orderbook: KalshiOrderbook | null
): number | null {
  if (!orderbook || order.queue_position == null) return null;

  const px = order.side === 'yes' ? order.yes_price : order.no_price;
  if (px == null) return null;

  // Get the orderbook levels for this order's side
  const levels = order.side === 'yes' ? orderbook.yes : orderbook.no;
  if (!levels) return null;

  // Sum all contracts at prices strictly better (higher) than ours
  let depth = 0;
  for (const [levelPrice, levelQty] of levels) {
    if (levelPrice > px) {
      depth += levelQty;
    }
  }

  // Add our queue position at own price level
  depth += order.queue_position;

  return depth;
}

export interface OrderManagementReturn {
  orders: KalshiOrder[];
  /** Velocity data per order ID (contracts/sec toward front of queue) */
  velocityByOrderId: Record<string, VelocityResult>;
  /** Absolute queue depth per order ID */
  absoluteQueueByOrderId: Record<string, number>;
  fillNotifications: FillNotification[];
  showOrderTicket: boolean;
  orderTicketMarket: string | undefined;
  orderTicketPrice: number | undefined;
  showKillAllConfirm: boolean;
  setShowKillAllConfirm: React.Dispatch<React.SetStateAction<boolean>>;
  portfolioRealtimeEnabled: boolean;
  refreshOrders: () => Promise<void>;
  handleFill: (fill: FillNotification) => void;
  handleOrderPlaced: () => Promise<void>;
  handlePlaceKalshiDashboardOrder: (args: {
    marketTicker: string;
    side: 'yes' | 'no';
    priceCents: number;
    contracts: number;
    isTakerMode: boolean;
    expirationTs?: number;
  }) => Promise<void>;
  handleCancelOrder: (orderId: string) => Promise<void>;
  handleDecreaseOrder: (orderId: string, nextQuantity: number) => Promise<void>;
  handleKillAll: () => Promise<void>;
  openOrderTicket: (marketTicker?: string, price?: number) => void;
  closeOrderTicket: () => void;
  cleanupOrders: () => void;
}

export function useOrderManagement({
  isConnected,
  apiClientRef,
  credentials,
  positionTrackerRef,
  marketsDataRef,
  refreshPositions,
  loadPositions,
  setErrorMessage,
  portfolioPollingEnabled,
  setPortfolioPollingEnabled,
  onActivity,
}: OrderManagementParams): OrderManagementReturn {
  const [orders, setOrders] = useState<KalshiOrder[]>([]);
  const [velocityByOrderId, setVelocityByOrderId] = useState<Record<string, VelocityResult>>({});
  const [absoluteQueueByOrderId, setAbsoluteQueueByOrderId] = useState<Record<string, number>>({});
  const [fillNotifications, setFillNotifications] = useState<FillNotification[]>([]);
  const [showOrderTicket, setShowOrderTicket] = useState(false);
  const [orderTicketMarket, setOrderTicketMarket] = useState<string | undefined>();
  const [orderTicketPrice, setOrderTicketPrice] = useState<number | undefined>();
  const [showKillAllConfirm, setShowKillAllConfirm] = useState(false);
  const [portfolioRealtimeEnabled, setPortfolioRealtimeEnabled] = useState(false);

  const orderMonitorRef = useRef<OrderMonitor | null>(null);
  const userStreamRef = useRef<UserStream | null>(null);
  const velocityTrackerRef = useRef(new QueueVelocityTracker({ ringSize: 120 }));
  /** Track order IDs that have received at least one fill (for first-sale ka-ching) */
  const filledOrderIdsRef = useRef<Set<string>>(new Set());
  /** Last orderbook fetch time per ticker (throttle to 15s) */
  const obFetchTimesRef = useRef(new Map<string, number>());
  /** Cached orderbooks per ticker */
  const obCacheRef = useRef(new Map<string, KalshiOrderbook>());

  const refreshOrders = useCallback(async () => {
    if (!apiClientRef.current) return;
    if (!portfolioPollingEnabled) return;
    try {
      const ordersData = await apiClientRef.current.getOpenOrders();
      setOrders(ordersData);

      // Feed queue positions to velocity tracker
      const tracker = velocityTrackerRef.current;
      const now = Date.now();
      for (const order of ordersData) {
        if (order.queue_position != null) {
          tracker.record(order.order_id, order.queue_position, now);
        }
      }

      // Check for velocity spikes and emit activity events
      const spikes = tracker.checkAllSpikes();
      for (const spike of spikes) {
        const order = ordersData.find((o) => o.order_id === spike.orderId);
        const dirLabel = spike.velocity.direction === 'advancing' ? 'advancing' : 'retreating';
        const msg = `Queue ${dirLabel} at ${Math.abs(spike.velocity.velocity).toFixed(1)} contracts/sec`;
        onActivity?.({
          type: 'queue_velocity',
          message: order ? `${order.ticker} — ${msg}` : msg,
          orderId: spike.orderId,
          ticker: order?.ticker,
        });
      }

      // Compute velocity for all tracked orders and expose via state
      const velocities: Record<string, VelocityResult> = {};
      for (const order of ordersData) {
        const v = tracker.getVelocity(order.order_id);
        if (v) velocities[order.order_id] = v;
      }
      setVelocityByOrderId(velocities);

      // Clean up tracker for filled/cancelled orders
      const activeIds = new Set(ordersData.map((o) => o.order_id));
      for (const orderId of [...tracker['rings'].keys()]) {
        if (!activeIds.has(orderId)) tracker.remove(orderId);
      }

      // Fetch orderbooks for absolute queue depth (throttled to 15s per ticker)
      const api = apiClientRef.current;
      if (api && ordersData.length > 0) {
        const uniqueTickers = [...new Set(ordersData.map((o) => o.ticker))];
        const fetchTimes = obFetchTimesRef.current;
        const cache = obCacheRef.current;
        const OB_THROTTLE_MS = 15_000;

        // Fetch stale tickers (fire-and-forget, don't block refreshOrders)
        const staleTickers = uniqueTickers.filter(
          (t) => !fetchTimes.has(t) || now - fetchTimes.get(t)! >= OB_THROTTLE_MS
        );

        if (staleTickers.length > 0) {
          // Don't await — let it run in background
          Promise.allSettled(
            staleTickers.map(async (ticker) => {
              fetchTimes.set(ticker, now);
              const ob = await api.getOrderbook(ticker);
              if (ob) cache.set(ticker, ob);
            })
          ).then((results) => {
            // Report any orderbook fetch failures
            const failures = results.filter((r) => r.status === 'rejected');
            if (failures.length > 0) {
              onActivity?.({
                type: 'error',
                message: `Orderbook fetch failed for ${failures.length}/${staleTickers.length} tickers`,
              });
            }
            // Recompute absolute queue depths with updated orderbooks
            const depths: Record<string, number> = {};
            for (const order of ordersData) {
              const ob = cache.get(order.ticker) ?? null;
              const depth = computeAbsoluteQueueDepth(order, ob);
              if (depth != null) depths[order.order_id] = depth;
            }
            setAbsoluteQueueByOrderId(depths);
          });
        } else {
          // Use cached orderbooks
          const depths: Record<string, number> = {};
          for (const order of ordersData) {
            const ob = cache.get(order.ticker) ?? null;
            const depth = computeAbsoluteQueueDepth(order, ob);
            if (depth != null) depths[order.order_id] = depth;
          }
          setAbsoluteQueueByOrderId(depths);
        }

        // Clean up cache for tickers no longer in orders
        const activeTickers = new Set(uniqueTickers);
        for (const ticker of [...cache.keys()]) {
          if (!activeTickers.has(ticker)) {
            cache.delete(ticker);
            fetchTimes.delete(ticker);
          }
        }
      }
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      if (
        msg.includes('API error: 401') ||
        msg.includes('"code":"authentication_error"') ||
        msg.includes('"code": "authentication_error"')
      ) {
        setPortfolioPollingEnabled(false);
        setErrorMessage(
          'Connected to market feed, but portfolio endpoints are unauthorized (401 authentication_error). ' +
            'This usually means you are using the wrong environment (prod vs demo) or your API key is not provisioned for trading/portfolio.'
        );
        return;
      }
      console.error('Failed to refresh orders:', error);
    }
  }, [
    apiClientRef,
    portfolioPollingEnabled,
    setErrorMessage,
    setPortfolioPollingEnabled,
    onActivity,
  ]);

  const handleFill = useCallback(
    (fill: FillNotification) => {
      setFillNotifications((prev) => [...prev, fill]);
      setTimeout(() => {
        setFillNotifications((prev) => prev.filter((f) => f.orderId !== fill.orderId));
      }, 5000);

      const market = marketsDataRef.current.get(fill.ticker);
      const currentPrice = market?.awayProb || market?.homeProb || null;
      positionTrackerRef.current.addFill(fill, currentPrice);

      onActivity?.({
        type: 'order_filled',
        message: `${fill.quantity} contracts ${fill.ticker} ${fill.side.toUpperCase()} @ ${fill.price}¢`,
        amount: (fill.quantity * fill.price) / 100,
        orderId: fill.orderId,
        ticker: fill.ticker,
      });

      // Imperative sound — priority: whale > ka-ching (first fill) > standard fill
      const snd = readSoundSettings();
      if (snd.tradeSoundEnabled) {
        const isFirstFill = !filledOrderIdsRef.current.has(fill.orderId);
        if (isFirstFill) filledOrderIdsRef.current.add(fill.orderId);

        if (fill.quantity >= 1000 && snd.whaleSoundEnabled) {
          playWhaleTone();
        } else if (isFirstFill && snd.firstSaleSoundEnabled) {
          playKaChingTone();
        } else {
          playFillTone();
        }
      }

      refreshPositions();
      refreshOrders();
    },
    [marketsDataRef, positionTrackerRef, refreshPositions, refreshOrders, onActivity]
  );

  // Init user stream + order monitor when connected
  useEffect(() => {
    if (!isConnected || !credentials || !apiClientRef.current) return;

    // Already have user stream
    if (userStreamRef.current) return;

    let cancelled = false;
    const apiClient = apiClientRef.current;

    // Load initial data
    refreshOrders();
    loadPositions();

    (async () => {
      try {
        const { privateKey: cryptoKey } = await initAuth(
          credentials.accessKeyId,
          credentials.privateKeyPem
        );

        if (cancelled) return;

        // Start user stream for fills/orders
        try {
          const userStream = createUserStream(true);
          userStreamRef.current = userStream;
          userStream.onFill((fill) => handleFill(fill));
          userStream.onOrderEvent(() => {
            refreshOrders();
          });

          // Relay-level connection events (fires even when upstream reconnects transparently)
          // Note: relayClient is internal to userStream, so we emit from the error/reconnect path
          userStream.onError((err) => {
            console.warn('User stream error (will fall back to polling):', err);
            setPortfolioRealtimeEnabled(false);

            if (!orderMonitorRef.current) {
              const fallback = new OrderMonitor();
              fallback.start(
                apiClient,
                {
                  onFill: (fill) => handleFill(fill),
                  onStatusChange: (orderId, oldStatus, newStatus) => {
                    console.log(`Order ${orderId} status: ${oldStatus} → ${newStatus}`);
                    refreshOrders();
                  },
                },
                { pollIntervalMs: 15000 }
              );
              orderMonitorRef.current = fallback;
            }
          });

          await userStream.connect(credentials.accessKeyId, cryptoKey, credentials.environment);
          userStream.subscribe();
          if (!cancelled) {
            setPortfolioRealtimeEnabled(true);
          }
        } catch (err) {
          console.warn('User stream unavailable, falling back to polling:', err);
          if (!cancelled) {
            setPortfolioRealtimeEnabled(false);
          }

          const fallback = new OrderMonitor();
          fallback.start(
            apiClient,
            {
              onFill: (fill) => handleFill(fill),
              onStatusChange: (orderId, oldStatus, newStatus) => {
                console.log(`Order ${orderId} status: ${oldStatus} → ${newStatus}`);
                refreshOrders();
              },
            },
            { pollIntervalMs: 5000 }
          );
          orderMonitorRef.current = fallback;
        }
      } catch (err) {
        console.error('Order management auth init failed:', err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    isConnected,
    credentials,
    apiClientRef,
    handleFill,
    refreshOrders,
    loadPositions,
    setPortfolioPollingEnabled,
  ]);

  // Polling intervals
  useEffect(() => {
    if (!isConnected) return;
    if (!portfolioPollingEnabled) return;

    const ordersInterval = setInterval(() => {
      refreshOrders();
    }, 5000);

    const positionsIntervalMs = portfolioRealtimeEnabled ? 30000 : 5000;
    const positionsInterval = setInterval(() => {
      refreshPositions();
    }, positionsIntervalMs);

    return () => {
      clearInterval(ordersInterval);
      clearInterval(positionsInterval);
    };
  }, [
    isConnected,
    portfolioPollingEnabled,
    portfolioRealtimeEnabled,
    refreshOrders,
    refreshPositions,
  ]);

  const handleOrderPlaced = useCallback(async () => {
    await refreshOrders();
    if (orderMonitorRef.current) {
      await orderMonitorRef.current.checkImmediate();
    }
  }, [refreshOrders]);

  const handlePlaceKalshiDashboardOrder = useCallback(
    async (args: {
      marketTicker: string;
      side: 'yes' | 'no';
      priceCents: number;
      contracts: number;
      isTakerMode: boolean;
      expirationTs?: number;
    }) => {
      const api = apiClientRef.current;
      if (!api) throw new Error('Not connected');

      const token = perfTracker.beginSpan('order', 'place-order', {
        ticker: args.marketTicker,
        side: args.side,
        contracts: args.contracts,
      });
      try {
        await api.placeOrder({
          ticker: args.marketTicker,
          side: args.side,
          action: 'buy',
          type: 'limit',
          count: args.contracts,
          price: args.priceCents,
          post_only: !args.isTakerMode,
          time_in_force: 'good_till_canceled',
          ...(args.expirationTs !== undefined ? { expiration_ts: args.expirationTs } : {}),
        });
        perfTracker.endSpan(token);
      } catch (err) {
        perfTracker.endSpan(token, { error: String(err) });
        throw err;
      }

      onActivity?.({
        type: 'order_placed',
        message: `${args.contracts} contracts ${args.marketTicker} ${args.side.toUpperCase()} @ ${args.priceCents}¢`,
        amount:
          (args.contracts * (args.side === 'yes' ? args.priceCents : 100 - args.priceCents)) / 100,
        ticker: args.marketTicker,
      });

      await refreshOrders();
      refreshPositions();
    },
    [apiClientRef, refreshOrders, refreshPositions, onActivity]
  );

  const handleKillAll = useCallback(async () => {
    if (!apiClientRef.current) return;
    if (!confirm('Are you sure you want to cancel ALL open orders?')) return;

    try {
      await apiClientRef.current.cancelAllOrders();
      await refreshOrders();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to cancel orders';
      setErrorMessage(message);
    }
  }, [apiClientRef, refreshOrders, setErrorMessage]);

  const handleCancelOrder = useCallback(
    async (orderId: string) => {
      if (!apiClientRef.current) return;
      const token = perfTracker.beginSpan('order', 'cancel-order', { orderId });
      try {
        await apiClientRef.current.cancelOrder(orderId);
        perfTracker.endSpan(token);
        onActivity?.({
          type: 'order_cancelled',
          message: `Order ${orderId.slice(0, 8)}… cancelled`,
          orderId,
        });
        velocityTrackerRef.current.remove(orderId);
        await refreshOrders();
      } catch (error) {
        perfTracker.endSpan(token, { error: String(error) });
        const message = error instanceof Error ? error.message : 'Failed to cancel order';
        setErrorMessage(message);
      }
    },
    [apiClientRef, refreshOrders, setErrorMessage, onActivity]
  );

  const handleDecreaseOrder = useCallback(
    async (orderId: string, nextQuantity: number) => {
      if (!apiClientRef.current) return;
      try {
        await apiClientRef.current.decreaseOrder(orderId, { reduce_to: nextQuantity });
        await refreshOrders();
      } catch (error) {
        const message =
          error instanceof Error ? error.message : 'Failed to decrease order quantity';
        setErrorMessage(message);
        throw error;
      }
    },
    [apiClientRef, refreshOrders, setErrorMessage]
  );

  const openOrderTicket = useCallback((marketTicker?: string, price?: number) => {
    setOrderTicketMarket(marketTicker);
    setOrderTicketPrice(price);
    setShowOrderTicket(true);
  }, []);

  const closeOrderTicket = useCallback(() => {
    setShowOrderTicket(false);
    setOrderTicketMarket(undefined);
    setOrderTicketPrice(undefined);
  }, []);

  const cleanupOrders = useCallback(() => {
    if (userStreamRef.current) {
      userStreamRef.current.disconnect();
      userStreamRef.current = null;
    }
    if (orderMonitorRef.current) {
      orderMonitorRef.current.stop();
      orderMonitorRef.current = null;
    }
    setOrders([]);
    setFillNotifications([]);
    setPortfolioRealtimeEnabled(false);
  }, []);

  return {
    orders,
    velocityByOrderId,
    absoluteQueueByOrderId,
    fillNotifications,
    showOrderTicket,
    orderTicketMarket,
    orderTicketPrice,
    showKillAllConfirm,
    setShowKillAllConfirm,
    portfolioRealtimeEnabled,
    refreshOrders,
    handleFill,
    handleOrderPlaced,
    handlePlaceKalshiDashboardOrder,
    handleCancelOrder,
    handleDecreaseOrder,
    handleKillAll,
    openOrderTicket,
    closeOrderTicket,
    cleanupOrders,
  };
}
